import { DeferredPromise } from '@cf-shared/shared-util';

// Backend-route resolution gate (#4405).
//
// WHAT THIS IS FOR
// ----------------
// A native production build talks either to the live server
// (mh-physique.app) or to the canary/staging server
// (canary.mh-physique.app). The choice is made at runtime from one signal: the
// version that is currently live in production, which the native side fetches
// and injects into the page as a global. This module is the part of the web
// side that waits for that value to arrive.
//
// WHY A WAIT EXISTS AT ALL (and why it used to not)
// -------------------------------------------------
// #4280 needed the value to be present the instant the web bundle started, and
// guaranteed that by HOLDING the very first boot of a fresh install: the native
// side refused to deliver the app's page until its probe answered. That hold
// cost us two things. It caused a boot crash on Android (#4404) that shipped in
// three consecutive canaries, and it left an offline first boot staring at a
// blank screen with no message and no way out, because no web code was running
// to draw one.
//
// #4405 removed the hold. The page now always loads immediately, which means
// the injected value can legitimately be missing for the first moments of a
// first-ever boot. Code that used to throw in that situation waits here
// instead. Nothing guesses a server: guessing is the #4273 lockout class, where
// a build newer than live was pinned to the live server, whose older code could
// not read its credentials, and the user was locked out with no explanation.
//
// See design-docs/backend-routing.md.
//
// This module is deliberately free of Angular, of the DOM and of `window`: it
// takes its two browser touchpoints as constructor arguments. That keeps the
// whole state machine unit-testable under plain Node
// (backend-route-gate.test.ts), which src/environments/ cannot be — the mocha
// glob in client/package.json excludes that directory. util/backend-origin.ts
// is the thin adapter that supplies the real window-backed functions.

/**
 * Thrown when a caller waited for the backend route and the native side never
 * answered within the bound.
 *
 * In practice this means one thing: the device could not reach the network on a
 * first-ever boot. `isNetworkError()` in cloud-functions-errors.ts recognises
 * this type for exactly that reason, so the app's existing retry and offline
 * handling takes over and this never surfaces as an unexplained failure.
 */
export class BackendRouteUnresolvedError extends Error {
  /** how long the caller waited before giving up, in milliseconds */
  readonly waitedMs: number;

  constructor(waitedMs: number) {
    super(
      `backend route still unresolved after ${waitedMs}ms — the native /version probe has not answered yet`,
    );
    this.name = 'BackendRouteUnresolvedError';
    this.waitedMs = waitedMs;
  }
}

/**
 * Holds the injected live-version string for one session, and lets callers wait
 * for it.
 *
 * THE THREE-LANGUAGE RACE CONTRACT. This class is one of three places that
 * implement the same handshake; the others are BackendRouteResolver.java and
 * MainViewController.swift. The rule is:
 *
 *   - The NATIVE side always sets the global FIRST, and only then calls the
 *     notify hook if one exists.
 *   - The WEB side always reads the global at construction, before it installs
 *     the hook or starts waiting.
 *
 * Together those two rules make the handshake safe in both orders, with no
 * locking. JavaScript runs one thing at a time, and a native
 * evaluateJavascript / evaluateJavaScript call runs as its own task, so it
 * cannot interleave with the constructor below. Either native got there first,
 * in which case the constructor finds the value already on the window, or it
 * gets there later, in which case the hook is already installed and gets
 * called. Exactly one of the two paths fires. Do not "simplify" either side to
 * do only one of set-then-notify or read-then-install — that reintroduces a
 * window where the answer is dropped and the boot waits forever for a value
 * that already arrived.
 */
export class BackendRouteGate {
  /** the answer, once we have it. undefined means "still unknown". */
  private liveVersion: string | undefined;

  /**
   * Resolved once, with the live version, when the answer lands.
   *
   * It is only ever RESOLVED, never rejected. Each caller applies its own
   * deadline by racing this against a timer, so a caller that gives up early
   * cannot leave a rejected promise behind that fails every later caller for
   * the rest of the session. (It also means there is never an unhandled
   * rejection sitting on this promise when nobody happens to be waiting.)
   */
  private readonly whenResolved = DeferredPromise.defer<string>();

  /**
   * @param readLiveVersion reads the currently injected value, or undefined if
   *   the native side has not injected one yet.
   * @param installNotifyHook publishes a callback for the native side to invoke
   *   once it has injected a value. Only called when the value is not already
   *   there, so a normal cached boot installs nothing at all.
   */
  constructor(
    private readonly readLiveVersion: () => string | undefined,
    installNotifyHook: (notify: () => void) => void,
  ) {
    // if the value is already on the window, take it and skip the hook entirely
    const alreadyInjected = readLiveVersion();
    if (alreadyInjected !== undefined) {
      this.acceptAnswer(alreadyInjected);
      return;
    }
    // otherwise let the native side wake us when its probe answers
    installNotifyHook(() => this.acceptAnswer(this.readLiveVersion()));
  }

  /**
   * Take an answer, if it is a real one. Deliberately ignores two cases rather
   * than reporting them, because neither is a fault:
   *
   *  - a wake with nothing set — resolving to undefined would hand a caller an
   *    empty origin, which is worse than continuing to wait;
   *  - a second, different answer — the route must not change underneath a
   *    session that has already started talking to one server. The native
   *    background re-probe updates the cache for the NEXT launch, and must
   *    never re-point this one.
   */
  private acceptAnswer(candidate: string | undefined): void {
    if (candidate === undefined) return;
    if (this.liveVersion !== undefined) return;
    this.liveVersion = candidate;
    this.whenResolved.resolve(candidate);
  }

  /**
   * The answer if we already have it, otherwise undefined. Never waits, never
   * throws.
   *
   * This is what the callers that must not wait use — the ones talking to
   * endpoints where the choice of server does not change the outcome. They fall
   * back to the live host while this returns undefined.
   */
  peekLiveVersion(): string | undefined {
    return this.liveVersion;
  }

  /**
   * Wait for the answer, up to `timeoutMs`.
   *
   * Resolves immediately when the answer is already known, which is the case on
   * every boot after the first, and on the first boot too once the probe has
   * landed. Rejects with `BackendRouteUnresolvedError` if the bound passes with
   * no answer — it never invents a server.
   */
  async whenResolvedAsync(timeoutMs: number): Promise<string> {
    if (this.liveVersion !== undefined) return this.liveVersion;

    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        this.whenResolved,
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => reject(new BackendRouteUnresolvedError(timeoutMs)), timeoutMs);
        }),
      ]);
    } finally {
      // always drop the timer, whichever side of the race won, so a resolved
      // wait does not hold a pending timeout open
      if (timer !== undefined) clearTimeout(timer);
    }
  }
}
